/*
 *   File : order.js
 *   Author : https://evoqins.com
 *   Description : Order data tab file
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

//Custom components
import { CustomDatePicker, CustomSearchInput, CustomSelectBox } from '../../../Components/FormElements';
import { PrimaryButton, SecondaryButton, TextButton } from '../../../Components/Buttons';
import { CustomDataTable } from '../../../Components/DataTable';
import { Chip, CustomTab, EmptyScreen } from '../../../Components/Others';
import { Icon } from '../../../Components/Icon';

//Style
import color from '../../../Style/color.scss';
import { APIService } from '../../../Service';

const TAB_TITLES = [
    { id: 1, label: 'All' },
    { id: 2, label: 'Goals' },
    { id: 3, label: 'Baskets' },
    { id: 4, label: 'Self managed' }
];

const Orders = () => {

    const { state } = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    const isRendered = useRef(true);
    const isClearRef = useRef(false);
    const scrollRef = useRef(null);

    const [searchValue, setSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTab, setSelectedTab] = useState(-1);
    const [searched, setSearched] = useState(false);


    const [toggleFilter, setToggleFilter] = useState(false);
    const [selectedType, setSelectedType] = useState(null);

    const [selectedOrderStatus, setSelectedOrderStatus] = useState(null);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);

    const [isPaginationChange, setIsPaginationChange] = useState(false);
    const [subAdminOrders, setSubAdminOrders] = useState({});
    const [orderFilterData, setOrderFilterData] = useState({});
    const [checked, setChecked] = useState([]);


    const [filterLoader, setFilterLoader] = useState(true);
    const [loader, setLoader] = useState(true);
    const [downloadLoader, setDownloadLoader] = useState(false);

    const [startDate, setStartDate] = useState(null);
    const [startDateToggle, setStartDateToggle] = useState(false);

    const [endDate, setEndDate] = useState(null);
    const [endDateToggle, setEndDateToggle] = useState(false);

    useEffect(() => {
        if (isClearRef.current === true) {
            setLoader(true);
            _subAdminOrder();
            isClearRef.current = false;

        }
    }, [isClearRef.current]);

    useEffect(() => {
        if (isRendered.current === true) {
            _filterOrder();
        }
        isRendered.current = false;
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, [isRendered]);


    useEffect(() => {

        if (selectedTab != -1) {
            _subAdminOrder();
            setCurrentPage(1);
            setLoader(true);
        }
    }, [selectedTab]);


    useEffect(() => {
        if (state !== null) {
            if (state.order_tab) {
                setSelectedTab(state.order_tab);
            } else {
                setSelectedTab(1);
            }
        } else {
        }
    }, [state]);

    // download list 
    const _downloadList = () => {
        _downloadOrder();
    };


    // handle tab change
    const _handleTab = (tab_id) => {
        setCurrentPage(1);
        navigate(`/sub-admins/${id}`, {
            state: {
                tab_index: state.tab_index,
                order_tab: tab_id
            }
        });
        setSelectedOrderStatus(orderFilterData.order_status[0]);
        setSelectedPaymentStatus(orderFilterData.payment_status[0]);
        setSelectedType(orderFilterData.order_type[0]);
        setToggleFilter(false);
        setSearchValue("");
    };

    const _handleClear = () => {
        setSelectedType(orderFilterData.order_type[0]);
        setSelectedOrderStatus(orderFilterData.order_status[0]);
        setSelectedPaymentStatus(orderFilterData.payment_status[0]);
        setStartDate(null);
        setEndDate(null);
        isClearRef.current = true;
    };


    // handle table page change 
    const _handleTablePageChange = (page_num) => {

        setCurrentPage(page_num);
        _subAdminOrder(page_num);
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    };

    const _onSearch = () => {
        setSearched(true);
        setLoader(true)
        _subAdminOrder();
    };

    const _handleSearchClear = () => {
        setSearched(false);
        setSearchValue("");
        if (searched === true) {
            _subAdminOrder(currentPage, "")
        }
    };

    // handle navigate to order detail 
    const _handleNavigate = (order_id) => {
        navigate(`/orders/${order_id}`);


    };

    // // handle navigate to order detail 
    // const _handleNavigate = (order_id) => {
    //     navigate(`${order_id}`, {
    //         state: {
    //             tab_index: selectedTab,
    //             current_page: currentPage
    //         }
    //     });
    // };

    // handle toggle filter 
    const _handleToggleFilter = () => {
        setToggleFilter(prev => !prev);
    };

    // handle select filter 
    const _handleSelectFilter = (selected, type) => {
        if (type === 1) {
            setSelectedType(selected);
            isClearRef.current = false;
        } else if (type === 2) {
            setSelectedOrderStatus(selected);
            isClearRef.current = false;
        } else {
            setSelectedPaymentStatus(selected);
            isClearRef.current = false;
        }
        isClearRef.current = false;
    };

    // handle filter
    const _handleFilter = () => {
        if (!selectedType && !selectedOrderStatus && !selectedPaymentStatus) {
            toast.dismiss();
            toast.success("Please select at least one filter", { type: "error" });
        } else {
            setLoader(true);
            _subAdminOrder();
        }
    };

    const _handleChecked = (customer_id) => {
        setChecked(prev => {
            if (prev.includes(customer_id)) {
                return prev.filter(id => id !== customer_id);
            } else {
                return [...prev, customer_id]
            }
        })
    };

    const _toggleAllRows = () => {
        if (checked.length === subAdminOrders.orders.length) {
            setChecked([]);
        } else {
            setChecked(subAdminOrders.orders.map(row => row.order_id));
        }
    };

    // handle toggle date picker 
    const _handleToggleStartDate = () => {
        setStartDateToggle(!startDateToggle);
    };

    // handle select date 
    const _handleSelectStartDate = (date) => {
        setStartDate(date);
        setStartDateToggle(false);
    };

    // handle clear date
    const _handleClearStartDate = () => {
        setStartDate(null);
        setEndDate(null);
    };



    // handle toggle date picker 
    const _handleToggleEndDate = () => {
        setEndDateToggle(!endDateToggle);
    };

    // handle select date 
    const _handleSelectEndDate = (date) => {
        setEndDate(date);
        setEndDateToggle(false);
    };

    // handle clear date
    const _handleClearEndDate = () => {
        setEndDate(null);
    };

    // Order data table column 
    const ORDER_TABLE_COLUMN = [
        {
            name: (
                // loader === false &&
                subAdminOrders.orders &&
                <Icon
                    icon={checked.length === subAdminOrders.orders.length ? 'checkbox-active' : 'checkbox-inactive'} // Toggle based on selection
                    size={16}
                    className='cursor-pointer'
                    onClick={() => _toggleAllRows()}  // Handle select/deselect all
                />
            ),
            cell: (row) => (
                <Icon
                    icon={checked.includes(row.order_id) ? 'checkbox-active' : 'checkbox-inactive'}
                    size={16}
                    onClick={() => _handleChecked(row.order_id)}  // Handle individual row selection
                />
            ),
            width: "70px"
        },
        {
            name: 'Order ID',
            selector: row => row.order_display_id,
            width: "180px"
        },
        {
            name: 'Customer ID',
            selector: row => row.customer_display_id,
            width: "140px"
        },
        {
            name: 'Customer Name',
            selector: row => row.name,
            width: "280px"
        },
        {
            name: 'Executed on',
            selector: row => row.order_date,
            width: "150px"
        },
        {
            name: 'Amount',
            selector: row => row.amount,
            width: "120px"
        },
        {
            name: 'Type',
            selector: row => row.type,
            width: "150px"
        },
        {
            name: 'Payment Status',
            cell: (row) =>
                <Chip label={row.payment_status}
                    className={row.payment_status === "Successful" ? "e-bg-green-crayola" :
                        row.payment_status === "Failed" ? "e-bg-maximum-red " :
                            row.payment_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />,
            width: "160px"
        },
        {
            name: 'Order Status',
            cell: (row) =>
                <Chip label={row.order_status}
                    tooltip={row.order_status === "Failed" ? true : false}
                    content={row.order_status === "Failed" ? row.failure_reason : ""}
                    className={row.order_status === "Successful" ? "e-bg-green-crayola" :
                        row.order_status === "Failed" ? "e-bg-maximum-red " :
                            row.order_status === "Pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"} />,
            width: "180px"
        },
        {
            name: 'Order status updated on',
            selector: row => row.order_status_updated_on,
            width: "220px"
        },

    ];

    // API - Sub admin orders
    const _subAdminOrder = (page_num = currentPage, search_value = searchValue) => {

        const formatted_start_date = startDate === null ? null : format(new Date(startDate), 'yyyy-MM-dd');
        const formatted_end_date = endDate === null ? null : format(new Date(endDate), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'sub-admin/orders';
        let request = {

            "investment_type": selectedTab === 1 ? null : selectedTab - 1, // 1-All 2-Goals 3-Baskets 4-Self Managed
            "type": selectedType?.value,
            "order_status": selectedOrderStatus?.value,
            "payment_status": selectedPaymentStatus?.value,
            "id": id,
            "query": search_value,
            "page_num": page_num,
            "page_size": 10,
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setSubAdminOrders(response.data);
            } else {
                setSubAdminOrders({ orders: [] });
            }
            setLoader(false);
            setIsPaginationChange(false);
        })
    };

    // API - user detail download
    const _downloadOrder = () => {

        const formatted_start_date = startDate === null ? null : format(new Date(startDate), 'yyyy-MM-dd');
        const formatted_end_date = endDate === null ? null : format(new Date(endDate), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'sub-admin/order-download';
        let request = {
            "id": id,
            "order_ids": checked,
            "query": searchValue === "" ? null : searchValue,
            "type": selectedType.value,
            "order_status": selectedOrderStatus.value,
            "payment_status": selectedPaymentStatus.value,
            "investment_type": selectedTab === 1 ? null : selectedTab - 1,
            "start_date": formatted_start_date,
            "end_date": formatted_end_date
        }
        setDownloadLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                // window.open(response.data.url, '_blank');
                const url = response.data.url;
                const link = document.createElement('a');
                link.href = url;
                link.download = ''; // Optional: specify a file name
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setChecked([]);
            } else {
                toast.dismiss();
                toast.success(response.message, { type: "error" })
            }
            setDownloadLoader(false);
        })

    };


    // API - order detail filter
    const _filterOrder = () => {
        let is_post = false;
        let url = 'orders/filter-data';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setOrderFilterData(response.data);
                setSelectedType(response.data.order_type[0]);
                setSelectedOrderStatus(response.data.order_status[0]);
                setSelectedPaymentStatus(response.data.payment_status[0]);
            }
            else {
                setOrderFilterData([]);
            }
            setFilterLoader(false);
        });
    };

    return (
        <div className='pb-4 min-vh-'>
            <div ref={scrollRef} className='padding-32px-lr padding-32px-top '>
                {/* Custom tab */}
                <div className='d-flex'>
                    <CustomTab data={TAB_TITLES}
                        selectedTab={selectedTab}
                        tooltip={false}
                        handleClick={_handleTab} />
                </div>

                <div className='pt-4 d-flex align-items-center justify-content-end gap-3'>
                    <SecondaryButton name="Download List"
                        disabled={loader === false && subAdminOrders.orders.length === 0 ? true : false}
                        className={downloadLoader ? "w-129px h-42px" : ""}
                        loader={downloadLoader}
                        onClick={_downloadList} />

                    {/* Search input */}
                    <CustomSearchInput placeholder="Search with order ID or investor ID"
                        type="text"
                        value={searchValue}
                        className="w-340px"
                        onSearch={() => _onSearch()}
                        onSearchClear={() => _handleSearchClear()}
                        onClear={_handleSearchClear}
                        onKeyPress={() => _onSearch()}
                        handleChange={(e) => {
                            setSearchValue(e);
                            if (e.length === 0) {
                                _handleSearchClear();
                            }
                        }} />

                    <div className={`${toggleFilter ? 'e-bg-blue-violet' : ''} ms-3 padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer`}
                        onClick={_handleToggleFilter}>
                        <Icon icon='filter'
                            size='1.25rem'
                            className="d-flex"
                            color={toggleFilter && color.white} />
                    </div>
                </div>
            </div>

            {/* Filter section */}
            {toggleFilter &&
                <div className='margin-30px-top e-bg-main-white padding-24px-tb padding-32px-lr'>
                    <div className='d-flex justify-content-end'>
                        <TextButton name="Clear all"
                            onClick={_handleClear}
                            disabled={isClearRef.current === true}
                            cursorPointer={'cursor-pointer'}
                        />
                    </div>
                    <div className='d-flex gap-4 padding-32px-bottom '>
                        {/* Type select */}
                        <CustomSelectBox title='Type'
                            placeholder='Select'
                            controlWidth='10rem'
                            options={orderFilterData.order_type}
                            isSearchable={true}
                            value={selectedType}
                            onChange={(data) => _handleSelectFilter(data, 1)} />

                        {/* Order status select */}
                        <CustomSelectBox title='Order status'
                            placeholder='Select'
                            controlWidth='10rem'
                            options={orderFilterData.order_status}
                            isSearchable={true}
                            value={selectedOrderStatus}
                            onChange={(data) => _handleSelectFilter(data, 2)} />

                        {/* Payment status select */}
                        <CustomSelectBox title='Payment status'
                            placeholder='Select'
                            controlWidth='10rem'
                            options={orderFilterData.payment_status}
                            isSearchable={true}
                            value={selectedPaymentStatus}
                            onChange={(data) => _handleSelectFilter(data, 3)} />

                        {/* Start date */}
                        <CustomDatePicker
                            label="Start date"
                            placeholder="Select"
                            selected={startDate}
                            type={2}
                            toggle={startDateToggle}
                            maxDate={endDate}
                            width="w-160px"
                            handleToggle={_handleToggleStartDate}
                            handleClose={_handleToggleStartDate}
                            handleClear={_handleClearStartDate}
                            handleSelect={_handleSelectStartDate}
                        />

                        {/* End date */}
                        <CustomDatePicker
                            label="End date"
                            placeholder="Select"
                            selected={endDate}
                            type={2}
                            width="w-160px"
                            className="end-0"
                            toggle={endDateToggle}
                            minDate={startDate}
                            handleToggle={_handleToggleEndDate}
                            handleClose={_handleToggleEndDate}
                            handleClear={_handleClearEndDate}
                            handleSelect={_handleSelectEndDate}
                        />
                    </div>

                    <PrimaryButton name='Filter now'
                        paddingBlock='padding-12px-tb padding-44px-lr'
                        onClick={_handleFilter} />
                </div>
            }

            <div className='pt-4 padding-32px-lr d-flex gap-1 align-items-center justify-content-end '>
                <div>
                    <img src={require("../../../Assets/Image/scroll-animation.gif")}
                        draggable={false}
                        className='e-rotate-270'
                        width={24}
                        height={24}
                        alt='scroll' />
                </div>
                <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-12px'>
                    Scroll horizontally for view more  information
                </p>
            </div>

            {/* Order list table */}
            {loader ?
                <div className='d-flex align-items-center justify-content-center min-vh-100 pt-4'>
                    <img src={require("../../../Assets/Image/loader.gif")}
                        draggable={false}
                        width={80}
                        height={80}
                        alt='loader'
                        className='text-center' />
                </div>
                :
                subAdminOrders.orders.length > 0 ?
                    <div className='e-data-table-wrapper pt-2 padding-32px-bottom' >
                        <CustomDataTable columns={ORDER_TABLE_COLUMN}
                            data={subAdminOrders.orders}
                            pagination={subAdminOrders.total_count > 10 ? true : false}
                            paginationPerPage={10}
                            paginationDefaultPage={subAdminOrders.current_page}
                            lastPage={subAdminOrders.total_page}
                            paginationTotalRows={subAdminOrders.total_count}
                            onChangePage={_handleTablePageChange}
                            rowClick={(row) => _handleNavigate(row.order_id)} />
                    </div>
                    :
                    <EmptyScreen
                        image={require('../../../Assets/Image/NotFound/no-data.png')}
                        title='No results found'
                        description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                    />
            }
        </div>
    )
}

export default Orders;